/** zh-CN catalogue `settings` (W3-09; English: src/features/settings/settings.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

/* Keyword messages keep the English words after the Chinese ones, so a search in either language finds the row. */
const catalog: MessageCatalog = {
  ns: 'settings',
  locale: 'zh-CN',
  messages: {
    title: '设置',
    searchLabel: '搜索设置',
    searchPlaceholder: '搜索设置、快捷键和规则',
    searchClear: '清除搜索',
    searchResults: '{count, plural, other {# 个匹配项}}',
    searchNone: '没有与“{query}”匹配的内容',
    searchNoneHint: '试试更少或其他的词。命令及其按键也会被搜索。',
    navLabel: '设置分区',
    navMatches: '{title}：{count, plural, =0 {没有匹配项} other {# 个匹配项}}',
    resetAll: '重置所有设置',
    resetAllTitle: '重置所有设置？',
    resetAllBody: '所有设置都会恢复为默认值，包括你的快捷键、音量和检查规则。你的项目不受影响。',
    resetAllConfirm: '全部重置',
    resetAllDone: '所有设置都已恢复为默认值。',
    backToChart: '返回谱面',
    backToLibrary: '返回谱面库',
    changed: '已更改',
    changedHint: '已从默认值更改',
    resetRow: '将“{title}”重置为默认值',
    resetAlreadyDefault: '已经是默认值',
    percentValue: '{value}%',
    timesValue: '{value}x',
    unitMs: '毫秒',
    unitSeconds: '秒',
    unitPx: 'px',
    unitTimes: 'x',
    sizeB: '{size} B',
    sizeKB: '{size} KB',
    sizeMB: '{size} MB',
    sizeGB: '{size} GB',
    sizeTB: '{size} TB',
    cancel: '取消',
    dismiss: '关闭',
    opensInNewTab: '在新标签页中打开',
    sectionGeneral: '常规',
    sectionGeneralDesc: '语言、经验模式、外观和提示。',
    sectionEditing: '编辑',
    sectionEditingDesc: '网格、时间轴、粘贴、输入框和新音符的默认值。',
    sectionAudio: '音频',
    sectionAudioDesc: '音量、延迟和播放。',
    sectionPreview: '预览与皮肤',
    sectionPreviewDesc: '预览的外观和播放、Phira 玩家设置和音符皮肤。',
    sectionKeyboard: '键盘',
    sectionKeyboardDesc: '键盘预设和每个快捷键，并解释冲突。',
    sectionChecker: '谱面检查',
    sectionCheckerDesc: '谱面检查要查找哪些问题，以及其阈值。',
    sectionStorage: '存储',
    sectionStorageDesc: '此浏览器中已用的空间、版本、回收站和备份。',
    sectionFeatures: '面板与工具',
    sectionFeaturesDesc: '各个面板和工具的偏好设置。',
    sectionAbout: '关于与许可',
    sectionAboutDesc: '关于 Phiedit 以及它使用的软件和字体。',
    groupGrid: '网格',
    groupTimeline: '时间轴',
    groupPaste: '粘贴',
    groupFields: '输入框',
    groupNoteDefaults: '新音符默认值',
    groupVolumes: '音量',
    groupLatency: '延迟',
    groupPlayback: '播放',
    groupPhira: 'Phira 玩家设置',
    groupThresholds: '阈值（RPE 自动检查参数）',
    locale: '语言',
    localeDesc: '整个编辑器的语言。曲名和文字线永远不会被翻译。',
    localeKeywords: '语言 中文 英文 简体 language locale english chinese',
    languageEnglish: 'English',
    languageChinese: '简体中文',
    level: '经验模式',
    levelDesc: '编辑器显示多少内容。谱面使用的任何内容都不会被隐藏。',
    levelKeywords: '引导 完整 新手 进阶 简单 专家 模式 guided full beginner advanced simple expert mode',
    levelGuided: '引导',
    levelGuidedDesc: '通俗的提示，更少的输入框和工具。每个命令仍在命令面板中。',
    levelFull: '完整',
    levelFullDesc: '所有属性、层级、原始数据面板和 RPE 术语。',
    theme: '主题',
    themeKeywords: '主题 颜色 深色 浅色 对比度 外观 theme colours colors dark light contrast appearance',
    themeDark: '深色',
    themeLight: '浅色',
    themeHighContrast: '高对比度',
    reducedMotion: '减少动画',
    reducedMotionDesc: '关闭界面动画（谱面预览仍正常播放）。',
    reducedMotionKeywords: '动画 运动 无障碍 animation motion accessibility',
    reducedMotionSystem: '跟随系统',
    reducedMotionOn: '开',
    reducedMotionOff: '关',
    uiScale: '界面大小',
    uiScaleDesc: '缩放文字、按钮和图标。预览和时间轴保留各自的缩放。',
    uiScaleKeywords: '缩放 大小 字体 文字 放大 缩小 zoom scale size font text bigger smaller',
    hintBar: '提示栏',
    hintBarDesc: '底部列出当前可执行操作的那一行。',
    hintBarSentences: '完整句子',
    hintBarKeys: '仅按键',
    hintBarOff: '关',
    firstUseTips: '快捷键提示',
    firstUseTipsDesc: '你用鼠标做某件事几次之后，会出现提示告诉你它的快捷键。',
    layout: '工作区布局',
    layoutDesc: '谱面工作区如何排列其面板。小屏幕会自动使用笔记本布局。',
    layoutKeywords: '布局 面板 编排 动画 审阅 笔记本 屏幕 layout panels compose animate review laptop screen',
    layoutCompose: '编排（音符优先）',
    layoutAnimate: '动画（运动优先）',
    layoutReview: '审阅（大预览）',
    layoutLaptop: '笔记本（小屏幕）',
    tour: '导览',
    tourDesc: '两分钟的编辑器之旅。重置后会再次提议进行导览。',
    tourStatusDone: '已完成',
    tourStatusNotYet: '尚未进行',
    tourStart: '开始导览',
    tourAgain: '再次导览',
    tourUnavailable: '此版本中没有导览。',
    defaultSnap: '新项目的节拍吸附',
    defaultSnapDesc: '新音符和关键帧吸附的网格：1/4 表示每拍四步。',
    defaultSnapKeywords: '吸附 网格 细分 拍 量化 snap grid division beat quantize',
    xGrid: 'X 网格线',
    xGridDesc: '音符轨道显示多少条竖向参考线，用于横向吸附音符。',
    snapPlayhead: '暂停时将播放头吸附到网格',
    snapPlayheadDesc: '播放暂停时，播放头会移到最近的网格线（与 RPE 相同）。',
    snapPlayheadKeywords: '播放头 暂停 吸附 网格 playhead pause snap grid',
    scrollSensitivity: '滚动速度',
    scrollSensitivityDesc: '每次滚轮或触控板滚动时时间轴移动的距离。',
    scrollSensitivityKeywords: '滚动 滚轮 触控板 速度 灵敏度 scroll wheel trackpad speed sensitivity',
    playheadPosition: '播放头高度',
    playheadPositionDesc: '播放时播放头停留的位置，从时间轴底部算起。',
    jumpOnUndo: '撤销时跳转到更改处',
    jumpOnUndoDesc: '撤销和重做时滚动到被更改的内容并选中它。',
    pasteMode: '粘贴',
    pasteModeDesc: '带预览时会先显示结果，单击后放置；立即粘贴则马上粘贴（RPE 的行为）。',
    pasteKeywords: '粘贴 剪贴板 复制 预览 paste clipboard copy ghost',
    pasteGhost: '带预览',
    pasteImmediate: '立即',
    pasteAnchor: '粘贴位置',
    pasteAnchorDesc: '粘贴的音符和关键帧从哪里开始。',
    pasteAnchorMouse: '鼠标处',
    pasteAnchorPlayhead: '播放头处',
    beatSyntax: '拍数输入框：“12.1”表示',
    beatSyntaxDesc: '如何解读带小数点的拍数。输入框总会显示它理解的结果。',
    beatSyntaxKeywords: '拍数 写法 小数 细分 输入框 beat syntax decimal rpe subdivision field',
    beatSyntaxDefault: '12.1 拍',
    beatSyntaxRpe: '12 + 1 个吸附步长（RPE）',
    beatDisplay: '拍数的显示方式',
    beatDisplayDesc: '拍数在输入框、列表和时间读数中的写法。',
    beatDisplayBar: '第 4 小节 . 第 1 拍 + 3/4',
    units: '单位',
    unitsDesc: '易读单位（%、秒）或 RPE 写入的原始数字。每个输入框仍可单独切换。',
    unitsKeywords: '单位 原始 百分比 易读 units raw rpe percent friendly',
    unitsFriendly: '易读',
    unitsRpe: 'RPE 原始',
    autoKey: '新会话中开启自动关键帧',
    autoKeyDesc: '在预览中拖动判定线时，无需按 K 即可在播放头处添加关键帧。',
    autoKeyKeywords: '自动 关键帧 录制 动画 auto key keyframe record animate',
    noteAbove: '方向',
    noteAboveDesc: '新音符从判定线的哪一侧落下。',
    noteAboveYes: '判定线上方',
    noteAboveNo: '判定线下方',
    noteFake: '假键',
    noteFakeDesc: '新音符只是装饰：永远不参与判定，也不计入数量。',
    noteSpeed: '速度',
    noteSpeedDesc: '新音符的速度倍数（1 = 判定线速度）。',
    noteYOffset: '高度偏移',
    noteYOffsetDesc: '与判定线之间的额外距离，以 RPE 单位计。',
    noteSize: '宽度',
    noteSizeDesc: '新音符的宽度倍数。',
    noteAlpha: '不透明度',
    noteAlphaDesc: '新音符的不透明度，0（不可见）到 255（不透明）。',
    noteVisibleTime: '出现时间',
    noteVisibleTimeDesc: '新音符只在被击打前这么多秒出现。',
    noteVisibleTimeOff: '始终可见',
    noteVisibleTimeOn: '有限制',
    volumeMaster: '主音量',
    volumeMusic: '音乐',
    volumeHitsounds: '打击音',
    volumeMetronome: '节拍器',
    volumeKeywords: '音量 声音 响度 音频 volume sound loudness audio',
    latency: '音频延迟校正',
    latencyDesc: '加到设备报告的延迟上，使音符与声音对齐。“校准”会为你测量它。',
    latencyKeywords: '延迟 偏移 同步 蓝牙 校准 音频 latency delay offset sync bluetooth calibrate audio',
    calibrate: '校准…',
    calibrateDone: '延迟校正已设为 {ms} 毫秒。',
    calibrateUnavailable: '此版本中不能校准。',
    hitsoundDelayTapFlick: '打击音延迟：Tap 和 Flick',
    hitsoundDelayDrag: '打击音延迟：Drag',
    hitsoundDelayDesc: '让这些打击音比音符更早（负数）或更晚（正数）播放。',
    spaceStops: '停止播放时',
    spaceStopsDesc: '停止播放时播放头去往何处。',
    spaceStopsKeywords: '空格 停止 返回 播放 space stop return playback',
    spaceStopsStay: '停留在停止处',
    spaceStopsReturn: '返回开始播放处',
    preservePitch: '降速时保持音高',
    preservePitchDesc: '降速播放时音乐保持音准。此时时间会略不精确；编辑器会提示何时如此。',
    preservePitchKeywords: '音高 速率 降速 速度 节奏 pitch rate slow speed tempo',
    previewMode: '预览打开时使用',
    previewModeDesc: '编辑模式显示参考线和控制柄；纯净模式只显示玩家看到的内容。',
    previewModeEdit: '编辑模式',
    previewModeClean: '纯净模式',
    renderScale: '渲染分辨率',
    renderScaleDesc: '在较慢的电脑上调低会更快；调高会更清晰。',
    renderScaleKeywords: '分辨率 画质 清晰 性能 resolution quality sharp performance dpi',
    renderScaleAuto: '自动',
    fpsCap: '帧率限制',
    fpsCapDesc: '限制预览重绘的频率（节省电量）。',
    fpsCapKeywords: '帧率 电量 性能 fps frame rate battery performance',
    fpsCapNone: '不限制',
    noteScale: '音符大小',
    noteScaleDesc: 'Phira 玩家设置：所有音符的大小。',
    phiraKeywords: 'Phira 玩家 预览 phira player preview',
    doubleHint: '高亮多押',
    doubleHintDesc: 'Phira 玩家设置：同时击打的音符使用高亮皮肤。',
    particles: '打击粒子',
    particlesDesc: 'Phira 玩家设置：打击特效周围的粒子。',
    aspect: '宽高比',
    aspectDesc: 'Phira 玩家设置：以不同于谱面自身的宽高比预览。',
    aspectOff: '使用谱面的',
    aspectOn: '覆盖',
    aggressiveCull: '激进音符剔除',
    aggressiveCullDesc: 'Phira 玩家设置（默认开启）：像 Phira 一样隐藏屏幕外的音符。',
    strictHitColor: '自动播放的打击颜色与 Phira 一致',
    strictHitColorDesc: 'Phira 在自动播放中会忽略染色的打击特效；保持开启即可看到相同效果。',
    msaa: '平滑边缘（MSAA）',
    msaaDesc: 'Phira 玩家设置：抗锯齿。会消耗性能。',
    apFcIndicator: 'AP/FC 判定线颜色指示',
    apFcIndicatorDesc: 'Phira 玩家设置（默认开启）：判定模式下判定线会跟随 Perfect / Good / Miss 的颜色阶梯。关闭后保持白色。',
    skinsTitle: '皮肤',
    skinsKeywords: '皮肤 资源包 音符 打击特效 导入 skin respack resource pack notes hit effect import zip phigros',
    skinsDesc: '皮肤决定预览中音符和打击特效的外观。导入 Phira 资源包（.zip），即可以使用该皮肤的玩家的视角查看谱面。',
    skinImport: '导入皮肤（.zip）',
    skinImporting: '正在导入…',
    skinDefaultLabel: '预览使用的皮肤',
    skinAutomatic: '自动',
    skinAutomaticDesc: '有本地文件中的 Phigros 皮肤时使用它，否则使用由 Phiedit 绘制的皮肤。',
    skinAutomaticNow: '推荐。当前使用 {name}。',
    skinProblems: '选择皮肤时跳过了：',
    skinSourceProcedural: '由 Phiedit 绘制',
    skinSourceLocal: '本地文件中的 Phigros 皮肤',
    skinSourceRespack: '导入的资源包',
    skinBy: '作者：{author}',
    skinInUse: '使用中',
    skinRemove: '移除皮肤“{name}”',
    skinRemoveTitle: '移除“{name}”？',
    skinRemoveBody: '该资源包将从此浏览器中删除。以后要使用请再次导入。',
    skinRemoveConfirm: '移除',
    skinRemoved: '已移除“{name}”。',
    skinInstalled: '现在使用“{name}”。',
    skinNotARespack: '“{name}”不是资源包。谱面请从谱面库打开。',
    skinInvalid: '无法使用此资源包。',
    skinImportFailed: '无法导入皮肤：{detail}',
    skinActivateFailed: '无法加载该皮肤；继续使用之前的皮肤。',
    skinNone: '还没有安装任何皮肤。',
    preset: '键盘预设',
    presetDesc: '默认预设专为此编辑器设计；RPE 预设保留 Re:PhiEdit 用户熟悉的按键。其余部分都是共用的。',
    presetKeywords: '键盘 预设 键位 默认 快捷键 keyboard preset rpe re:phiedit default keys hotkeys',
    presetDefault: '默认',
    presetDefaultDesc: 'Q W E R 放置音符，空格播放，Ctrl+K 打开命令面板。',
    presetRpe: 'RPE',
    presetRpeDesc: 'I O P 播放和停止，Ctrl+J/K/L 设置速率，R … R 放置区段。',
    altFree: '免 Alt 键位',
    altFreeDesc:
      '适用于自行占用 Alt 的系统：每个 Alt 快捷键都会改用其他按键，或交给菜单和命令面板，拖动时用 Ctrl+Shift 代替 Alt。',
    altFreeKeywords: '免 Alt 窗口管理器 alt free linux window manager option',
    conventionsOfferRpe: '同时使用 RPE 的编辑习惯吗？（立即粘贴，“12.1” = 12 + 1 个吸附步长，RPE 单位）',
    conventionsOfferDefault: '同时使用默认的编辑习惯吗？（带预览粘贴，小数拍数，易读单位）',
    conventionsApply: '使用',
    conventionsApplied: '已更新编辑习惯。',
    keymapTitle: '快捷键',
    keymapKeywords: '快捷键 键位 按键 绑定 冲突 shortcut shortcuts keymap hotkey hotkeys keys rebind binding conflict',
    kmDesc:
      '单击一个按键可修改它，单击加号按钮可再添加一个按键，单击叉号可移除一个。你添加的按键在两种预设中都有效；你移除的按键只在其所属的预设中保持移除。“导出”会带上你的所有更改。',
    kmSearchLabel: '搜索快捷键',
    kmSearchPlaceholder: '搜索命令、按键或使用场景',
    kmSearchCaptureLabel: '按下按键来查找命令',
    kmSearchCapturePlaceholder: '请按下按键…',
    kmFindByKeys: '按下按键来查找命令',
    kmFilterLabel: '显示',
    kmFilterAll: '全部',
    kmFilterChanged: '已更改（{count}）',
    kmFilterConflicts: '冲突（{count}）',
    kmFilterUnbound: '没有按键',
    kmImport: '导入…',
    kmExport: '导出',
    kmImportRpe: '导入 RPE Hotkey.txt…',
    kmResetAll: '重置所有快捷键',
    kmNothingToReset: '你没有更改任何快捷键。',
    kmCount: '{count, plural, other {# 个命令}}，共 {total} 个',
    kmNone: '没有匹配的命令。',
    kmNoneForQuery: '没有与“{query}”匹配的命令。',
    kmPageFilter: '只列出与“{query}”匹配的命令。',
    kmNoneForKeys: '没有命令使用这些按键。',
    kmColCommand: '命令',
    kmColContext: '场景',
    kmColKeys: '按键',
    kmColActions: '操作',
    kmNoKeys: '没有按键',
    kmUnknownCommand: '此版本中没有',
    legendRed: '红色：两个命令在同一场景下共用一个按键；只有一个有效。请修改其中一个。',
    legendYellow: '共用按键（提示信息）：一个按键在不同场景下执行不同的操作；由场景决定。无需修正。',
    legendYours: '由你更改',
    legendMac: '在 macOS 上，快捷键中的 Ctrl 是 Cmd。',
    chipChange: '修改“{command}”的 {chord}',
    chipRemove: '从“{command}”中移除 {chord}',
    chipRestore: '为“{command}”恢复 {chord}',
    chipRemovedSr: '（已移除）',
    chipRemovedIn: '已在 {preset} 中移除',
    chipRemovedInHint: '{chord} 只在 {preset} 中被移除；你现在使用的按键不受影响。',
    chipRestoreIn: '在 {preset} 中为“{command}”恢复 {chord}',
    chipRemovedStale: '无效',
    chipRemovedStaleHint: '对 {chord} 的这项移除已不再对应任何预设按键，因此没有效果。',
    chipForget: '忘记“{command}”对 {chord} 的移除',
    announceForgot: '已忘记“{command}”对 {chord} 的移除。',
    kmCapturing: '按下任意按键即可查找使用它们的命令。Esc 停止；Tab 离开输入框。',
    conflictRedShort: '冲突',
    conflictYellowShort: '在其他场景中共用',
    conflictRed: '{chord} 在同一场景下还会运行“{other}”（{context}），因此其中只有一个有效。',
    conflictYellow: '{chord} 在另一场景（{context}）中还会运行“{other}”；由场景决定。',
    conflictFixHint: '请修改或移除两者之一。',
    addShortcut: '为“{command}”添加快捷键',
    resetCommand: '将“{command}”重置为预设按键',
    commandWithArgs: '{command}：{args}',
    announceSaved: '{chord} 现在运行“{command}”。',
    announceRemoved: '已从“{command}”中移除 {chord}。',
    announceRestored: '已为“{command}”恢复 {chord}。',
    announceReset: '“{command}”已重新使用预设按键。',
    issuesTitle: '{count, plural, =1 {你的一个快捷键无法使用} other {你的快捷键中有 # 个无法使用}}',
    issuesDrop: '移除它们',
    issuesDropped: '已移除无法使用的快捷键。',
    importDoneTitle: '已导入快捷键。',
    importPartialTitle: '已导入快捷键，但有问题：',
    importFailedTitle: '无法导入快捷键：',
    importFailedToast: '无法导入快捷键：{error}',
    importUnreadable: '无法读取该文件。',
    openKeymap: '打开快捷键',
    exportDone: '你的快捷键已保存为 phiedit-keymap.json。',
    rpeImported: '已从 RPE 导入 {count, plural, other {# 个快捷键}}。',
    rpeUnmapped: '有 {count, plural, other {# 个 RPE 功能}}在这里没有对应：{names}',
    rpeImportUnavailable: '此版本中不能导入 RPE 的 Hotkey.txt 文件。',
    rpeImportNone: '在此文件中没有找到 RPE 快捷键。',
    rpeImportFailedTitle: '没有从 RPE 导入任何内容：',
    rpeImportedPartialToast:
      '已从 RPE 导入 {count, plural, other {# 个快捷键}}；有 {unmapped, plural, other {# 个 RPE 功能}}在这里没有对应。',
    resetShortcutsTitle: '重置所有快捷键？',
    resetShortcutsBody: '你的 {count, plural, other {# 处更改}}将被移除，并恢复预设按键。',
    resetShortcutsConfirm: '重置快捷键',
    resetShortcutsDone: '所有快捷键都已恢复为预设。',
    recordedToast: '已保存快捷键：{chord}',
    ctxEverywhere: '所有位置',
    ctxOrSeparator: ' 或 ',
    ctxNot: '非“{what}”',
    ctxLane: '音符轨道',
    ctxMotion: '运动列',
    ctxCurve: '曲线编辑器',
    ctxPreview: '预览',
    ctxLines: '判定线面板',
    ctxInspector: '属性面板',
    ctxTiming: '曲目与时间',
    ctxDock: '停靠面板',
    ctxProblems: '问题',
    ctxHistory: '历史',
    ctxAssets: '素材',
    ctxRaw: '原始数据面板',
    ctxPublish: '发布',
    ctxLibrary: '谱面库',
    ctxSettings: '设置',
    ctxTextInput: '正在输入框中输入',
    ctxDialog: '在对话框中',
    ctxPalette: '命令面板',
    ctxPlacingHold: '正在放置 Hold',
    ctxPlacingSegment: '正在放置区段',
    ctxPasteGhost: '粘贴预览',
    ctxGesture: '正在拖动',
    ctxPlaying: '播放时',
    ctxSelNotes: '已选中音符',
    ctxSelKeys: '已选中关键帧',
    ctxSelSegments: '已选中区段',
    ctxSelLines: '已选中判定线',
    ctxSelBpm: '已选中速度点',
    ctxSelAny: '已选中内容',
    ctxSelSingle: '已选中一项',
    ctxSelMixed: '混合选择',
    ctxColumnFocused: '已聚焦一列',
    ctxProjectOpen: '已打开项目',
    ctxUiMover: '当前为 UI 绑定线',
    ctxNotesAllowed: '当前判定线可放置音符',
    ctxWorkspaceTiming: '曲目与时间工作区',
    ctxWorkspaceChart: '谱面工作区',
    ctxWorkspacePublish: '发布工作区',
    ctxLevelGuided: '引导模式',
    ctxLevelFull: '完整模式',
    ctxPresetDefault: '默认键位',
    ctxPresetRpe: 'RPE 键位',
    ctxOsMac: 'macOS',
    ctxOsOther: 'Windows 和 Linux',
    ctxToolSelect: '选择工具',
    ctxToolTap: 'Tap 工具',
    ctxToolDrag: 'Drag 工具',
    ctxToolFlick: 'Flick 工具',
    ctxToolHold: 'Hold 工具',
    ctxToolEraser: '橡皮擦',
    catFile: '文件',
    catEdit: '编辑',
    catSelect: '选择',
    catView: '视图',
    catPlayback: '播放',
    catNotes: '音符',
    catMotion: '运动',
    catLines: '判定线',
    catTiming: '时间',
    catTools: '工具',
    catPublish: '发布',
    catHelp: '帮助与设置',
    catUnknown: '其他',
    recorderTitleAdd: '为“{command}”添加新快捷键',
    recorderTitleChange: '修改“{command}”的快捷键',
    recorderDesc: '按下你想要的组合键。Esc 取消。',
    recorderPrompt: '请现在按下按键',
    recorderHolding: '{mods}+ …',
    modCtrl: 'Ctrl',
    modCmd: 'Cmd',
    modAlt: 'Alt',
    modOption: 'Option',
    modShift: 'Shift',
    recorderListeningLabel: '正在录制：请按下组合键',
    recorderCapturedLabel: '已录制 {chord}。按下即可重新录制。',
    recorderHintListening:
      '每个按键都会被记录，包括 Tab 和 Enter。按键按位置记录，因此在任何键盘布局下都在相同的手指下。',
    recorderHintCaptured: '单击此框或在其上按 Enter，即可录制其他按键。',
    recorderReplaces: '替换',
    recorderSame: '这已经是该快捷键。',
    recorderFree: '可用：此处没有其他命令使用这些按键。',
    recorderConflictRed: '在同一场景下已经运行“{command}”（{context}）。',
    recorderConflictYellow: '在另一场景（{context}）中还会运行“{command}”；由场景决定。',
    recorderTakeOver: '{count, plural, =1 {从“{command}”中移除这些按键} other {从其他 # 个命令中移除这些按键}}',
    recorderByChar: '匹配输入的字符，而不是按键位置',
    recorderByCharUnavailable: '请先录制一个会输入字符的按键。',
    recorderHold: '按住（按键按下期间持续运行）',
    recorderHoldUnavailable: '字符快捷键不能按住。',
    recorderSave: '保存',
    recorderSaveTakeOver: '保存并解决冲突',
    recorderNeedKey: '请先按下一个组合键。',
    recorderRefusedSave: '浏览器或系统占用了这些按键；请选择其他按键。',
    recorderSameSave: '这已经是该快捷键。',
    reasonTabsWindows: '浏览器用 {chord} 打开、关闭或恢复标签页和窗口；请换一个按键。',
    reasonTabSwitch: '浏览器保留 {chord} 用于切换标签页；请换一个按键。',
    reasonHistory: '浏览器用 {chord} 后退或前进，这会离开编辑器；请换一个按键。',
    reasonCloseWindow: '{chord} 会关闭浏览器窗口；请换一个按键。',
    reasonFullscreen: '{chord} 会让浏览器切换到全屏；请换一个按键。',
    reasonDevtools: '{chord} 会打开浏览器的开发者工具；请换一个按键。',
    reasonPrivateWindow: '{chord} 会打开隐私窗口；请换一个按键。',
    reasonAddons: '{chord} 会打开浏览器的扩展或标签页搜索；请换一个按键。',
    reasonBrowserTool: '{chord} 被浏览器自身占用；请换一个按键。',
    reasonScreenshot: '{chord} 会在浏览器中截图；请换一个按键。',
    reasonReload: '{chord} 会重新加载页面；请换一个按键。',
    reasonClearData: '{chord} 会打开浏览器的清除浏览数据窗口；请换一个按键。',
    reasonMacApp: '在 macOS 上，{chord} 会退出浏览器，或打开、关闭标签页和窗口；请换一个按键。',
    reasonMacWindow: '在 macOS 上，{chord} 会隐藏或最小化浏览器；请换一个按键。',
    reasonMacPrefs: '在 macOS 上，{chord} 会打开浏览器设置；请换一个按键。',
    reasonMacCycle: '在 macOS 上，{chord} 会在浏览器窗口之间切换；请换一个按键。',
    reasonMacHistory: '在 macOS 上，{chord} 会让浏览器后退或前进，这会离开编辑器；请换一个按键。',
    reasonMacAddress: '在 macOS 上，{chord} 会跳到地址栏；请换一个按键。',
    reasonMacLogout: '在 macOS 上，{chord} 会注销登录；请换一个按键。',
    reasonMacOther: '在 macOS 上，{chord} 被浏览器或系统占用；请换一个按键。',
    reasonCtrlAlt: '在许多键盘布局中 Ctrl+Alt 等同于 AltGr（用于输入 @ 或欧元符号等字符）；请换一个按键。',
    reasonUnreliableMenu: '部分浏览器用 {chord} 打开自己的菜单，因此它不一定总能传到 Phiedit。菜单和命令面板仍然可用。',
    reasonUnreliableAddress: '在部分浏览器中，{chord} 可能会把焦点移到地址栏。菜单和命令面板仍然可用。',
    reasonUnreliableDownloads: '在部分浏览器中，{chord} 会打开下载列表。菜单和命令面板仍然可用。',
    reasonUnreliableHistory: '在部分浏览器中，{chord} 会打开浏览历史。菜单和命令面板仍然可用。',
    reasonUnreliableOther: '部分浏览器自己使用 {chord}，因此它不一定总能传到 Phiedit。',
    thresholdXMax: '音符横向过远',
    thresholdXMaxDesc: '距离判定线中心超过此值的音符会得到一条提示（“X 过大”）。',
    thresholdKeywords: '阈值 限制 自动检查 参数 threshold limit auto check rpe parameters',
    thresholdCollision: '碰撞距离',
    thresholdCollisionDesc: '水平距离小于此值的音符算作重叠，或算作紧接在 Flick 之后的 Tap。',
    thresholdReadTime: '最短读谱时间',
    thresholdReadTimeDesc: '被击打前可见时间短于此值的音符会得到一条警告。',
    thresholdDragWindow: 'Drag 警告窗口',
    thresholdDragWindowDesc: '在附近 Drag 之后这么快出现的 Tap 会得到一条警告。',
    thresholdComboWindow: '连接窗口',
    thresholdComboWindowDesc: '在附近 Flick 之后此时间内的 Tap 是错误（粉接八），在 Drag 之后则是提示。',
    thresholdNearSimultaneous: '几乎同时',
    thresholdNearSimultaneousDesc: '时间差小于此值、但不完全同时的音符会得到一条提示。',
    rulesTitle: '规则',
    rulesKeywords: '规则 检查 问题 警告 错误 提示 关闭 开启 rules checker problems warnings errors tips disable enable',
    rulesDesc: '关闭你不需要的规则。会导致 Phira 无法加载谱面的问题在导出时永远不会被隐藏。',
    rulesEnableAll: '开启所有规则',
    rulesAllOn: '全部已开启。',
    rulesAllOff: '全部已关闭。',
    rulesCategoryOn: '全部开启',
    rulesCategoryOff: '全部关闭',
    rulesOnCount: '已开启 {on} 条，共 {total} 条。',
    rulesNone: '谱面检查运行时会显示规则列表。',
    rulesNoneBody: '打开一张谱面：谱面检查会随之启动。',
    rulesNoMatch: '没有与“{query}”匹配的规则。',
    ruleCatPhira: 'Phira 兼容性',
    ruleCatPhiraDesc: 'Phira 无法加载、渲染错误或会忽略的内容。',
    ruleCatGameplay: '玩法',
    ruleCatGameplayDesc: '让谱面不公平或难以读懂的内容。',
    ruleCatStyle: '风格',
    ruleCatStyleDesc: '整洁度和 RPE 习惯。',
    storageTitle: '存储',
    storageKeywords:
      '存储 空间 配额 磁盘 回收站 备份 版本 持久化 下载 storage space quota disk trash backup versions persistence download',
    storageDesc: '项目、版本和媒体文件保存在此设备的这个浏览器中。请不时备份。',
    storageLoading: '正在读取存储信息…',
    storageUnavailable: '存储信息暂时不可用。',
    storageUsage: '已使用 {used}，共 {quota}',
    persistOn: '即使磁盘快满了，浏览器也会保留你的数据。',
    persistOff: '磁盘快满时，浏览器可能会删除你的数据。',
    persistRequest: '保留我的数据',
    persistGranted: '浏览器会保留你的数据。',
    persistDenied: '浏览器没有同意。将 Phiedit 安装为应用或加入书签通常会有帮助；在此期间请保留备份。',
    opfsOn: '媒体文件保存在浏览器文件系统中（快速）。',
    opfsOff: '媒体文件保存在浏览器数据库中（此浏览器没有私有文件系统；较慢但安全）。',
    saveOk: '所有更改都已保存。',
    savePending: '正在保存 {count, plural, other {# 项更改}}。',
    saveError: '保存失败：{error}',
    saveRestarting: '保存进程正在重启；在它恢复之前，你的更改会被保留。',
    versionsTitle: '版本',
    versionsRetention:
      'Phiedit 会保留最近一小时内的每个版本、两天内每小时一个版本、30 天内每天一个版本。命名的检查点和导出总会保留。',
    versionsBudget:
      '每个项目的版本最多可使用 {budget}（200 MB 与浏览器分配给 Phiedit 空间的十分之一中较小的那个）。超出时，最旧的自动版本会被逐步精简。',
    projectsTitle: '项目（{count}）',
    projectsNone: '还没有项目。',
    projectsTotal: '它们共使用 {size}，按大小从大到小排列。',
    projectsShowAll: '显示全部 {count} 个项目',
    projectsShowLargest: '只显示最大的 {count} 个',
    projectsColName: '项目',
    projectsColSize: '大小',
    projectsColUpdated: '修改时间',
    projectUntitled: '未命名',
    trashTitle: '回收站（{count}）',
    trashDesc: '删除的项目会在这里保留 {days} 天，之后被永久删除。',
    trashMeta: '删除于 {date} · {days, plural, =0 {今天永久删除} other {还剩 # 天}} · {size}',
    trashRestore: '恢复',
    trashRestored: '已将“{name}”恢复到谱面库。',
    trashDelete: '永久删除',
    trashDeleteOneTitle: '永久删除“{name}”？',
    trashEmpty: '清空回收站',
    trashEmptyTitle: '永久删除 {count, plural, other {# 个项目}}？',
    trashDeleteBody: '它们的版本以及没有其他项目使用的媒体文件也会被删除。此操作无法撤销。',
    trashDeleteConfirm: '永久删除',
    trashEmptyConfirm: '清空回收站',
    trashDeleted: '已永久删除 {count, plural, other {# 个项目}}。',
    storageBusy: '请稍候…',
    storageActionFailed: '操作失败；没有做任何更改。请重试。',
    backupTitle: '备份',
    backupDesc: '将所有项目下载为一个 .zip：每张谱面为一个 .pez 谱面包，并附带其 Phiedit 项目数据。',
    backupDownload: '全部下载为备份',
    backupRunning: '正在准备备份。',
    backupNothing: '还没有可备份的项目。',
    backupProgress: '正在准备备份',
    backupDone: '已将 {count, plural, other {# 个项目}}保存到备份文件。',
    backupFailed: '无法创建备份：{detail}',
    aboutTitle: '关于 Phiedit',
    aboutKeywords: '关于 许可 致谢 版本 字体 开源 about licence license credits version fonts open source phira',
    aboutName: 'Phiedit',
    aboutTagline: '在浏览器中运行的 Phira 谱面编辑器。',
    aboutBuildPublic: '公开版本：只包含以自由许可发布的素材。',
    aboutBuildLocal: '用于开发的本地版本。',
    aboutLocalSkin: 'Phigros 皮肤从这台电脑的本地文件读取，仅用于开发；它永远不会包含在公开版本中。',
    aboutCleanRoom:
      'Phiedit 不包含任何来自 Phira（GPL-3.0）或 Re:PhiEdit 的代码。与 Phira 的兼容性依据其公开文档、谱面格式以及 Phira 可观察到的行为实现。',
    aboutIndependent:
      'Phiedit 是一个独立项目。Phira、Phigros 和 Re:PhiEdit 归各自的所有者所有，他们并未为 Phiedit 背书。',
    licLibraries: '库',
    licFonts: '字体',
    licDocuments: '文档',
    licColName: '名称',
    licColUse: '用途',
    licColLicence: '许可',
    licFontsNote: '这些字体以 SIL 开放字体许可证 1.1 授权，可随 Phiedit 自由使用和分享。',
    useReact: '界面',
    useRadix: '无障碍的对话框、菜单和控件',
    useLucide: '图标',
    useVirtual: '长列表',
    usePanels: '可调整大小的面板',
    useCmdk: '命令面板',
    useZustand: '编辑器状态',
    useFflate: '读写 .pez 和 .zip 文件',
    useGif: '谱面中的 GIF 图片',
    useIdb: '浏览器中的存储',
    useYaml: '读写 info.yml',
    useNotoSc: '中文和日文文字：界面、曲名、文字线',
    useNoto: '界面文字',
    useSaira: '预览中的分数和连击数字',
    usePhiraDocs: 'RPE 谱面格式、info.yml 和 extra.json 的参考资料',
    cmdOpen: '打开设置',
    cmdKeymapOpen: '快捷键',
    cmdToggleLevel: '完整模式（显示所有工具）',
    cmdSetLanguage: '更改语言…',
    cmdSetTheme: '更改主题…',
    cmdSetHintBar: '提示栏样式…',
    cmdResetAll: '重置所有设置…',
    cmdApplyConventions: '使用某个键盘预设的编辑习惯',
    cmdSetPreset: '键盘预设…',
    cmdToggleAltFree: '免 Alt 键位',
    cmdRecord: '修改快捷键…',
    cmdImport: '导入快捷键（JSON）…',
    cmdExport: '导出快捷键（JSON）',
    cmdImportRpe: '导入 RPE Hotkey.txt…',
    cmdResetShortcuts: '重置所有快捷键…',
    paletteSection: '设置：{section}',
    paletteIn: '设置 > {section}',
    helpSettingsTitle: '设置',
    helpSettingsBody:
      '用 Ctrl+,（Mac 上为 Cmd+;）、从项目菜单打开“设置”，或在命令面板中输入“:”直接跳到某项设置。每项更改都会立即生效；设置旁的“重置”会恢复其默认值。设置属于你，而不属于某张谱面：它们在每个项目中都相同。',
    helpShortcutsTitle: '修改快捷键',
    helpShortcutsBody:
      '“设置 > 键盘”列出了每个命令。选择默认或 RPE 预设，然后单击一个按键修改它，单击加号再添加一个，或单击叉号移除一个。浏览器自身保留的按键会被拒绝并说明原因。红色表示两个命令在同一场景下共用一个按键（请修正）；黄色表示由场景决定。将快捷键导出为 JSON，即可带到另一台电脑上使用。',
    kmFilterShared: '共用按键（{count}）',
  },
};

export default catalog;
